<template>
  <div id="wrapper">
    <LoaderComponent v-show="!loaded" />
    <!-- Main "inner" page for displaying content -->
    <div v-show="loaded" id="explore-container">
      <!-- Side navigation component, which is passed current props -->
      <!-- Emissions trigger functions which set breadcrumbs -->
      <SideNavigation
        :theme="theme"
        :country="country"
        :filter="filter"
        :territory="territory"
        :location="location"
        :monument="monument"
        :itinerary="itinerary"
        :subItinerary="subItinerary"
        :subItineraryLocationIndex="subItineraryLocationIndex"
        :subItineraryLocation="currentLocation"
        :subItineraryLocationId="subItineraryLocationId"
        @go-to="setCurrent"
        @side-load="loadCurrent"
        @change-location="setLocation"
        @save-location="saveLocation"
      >
      </SideNavigation>

      <!-- Content -->
      <div id="explore">
        <!-- Breadcrumb links -->
        <div id="breadcrumb" v-if="breadcrumbs">
          <span><router-link :to="{ name: 'home' }">Home</router-link></span>
          <!-- Refer to computed prop -->
          <span v-for="(crumb, index) in breadcrumbs" :key="crumb[0]">
            <!-- ">" is part of the text! -->
            >
            <a
              v-if="!locationBreadcrumb(breadcrumbs, index)"
              :href="`/${crumb[1]}`"
            >
              {{ crumb[0] }}
            </a>
            <router-link v-else :to="locationRoute">
              {{ crumb[0] }}
            </router-link>
          </span>
        </div>

        <!-- Suggested Routes link (on Locations and Monuments with Routes) -->
        <!-- Only available on Explore by Country path -->
        <div id="suggested-routes-link"
          v-if="(($route.name === 'countries' && this.location && !this.route) && 
            (exploreData.relatedRoutes && exploreData.relatedRoutes.length > 0)) ||
            (($route.name === 'countries' && this.monument) &&
            (exploreData.locationHasRoutes))">
          <router-link :to="{ hash: '#suggested-routes' }" v-if="!monument">
            EXPLORE SUGGESTED ROUTES FOR {{ exploreData.name.toUpperCase() }}
          </router-link>
          <router-link v-else
            :to="{ 
              name: 'countries', 
              params: {
                country: $route.params.country,
                filter: $route.params.filter,
                territory: $route.params.territory,
                location: $route.params.location,
              },
              hash: '#suggested-routes',
            }">
            <!-- Explore Suggested Routes For {{ returnValueInLanguage("en", exploreData.locationName).value }} -->
            EXPLORE SUGGESTED ROUTES FOR {{ returnValueInLanguage("en", exploreData.locationName).value.toUpperCase() }}
          </router-link>
        </div>

        <!-- Route menu (for top of page once on a Route) -->
        <!-- Only available on Explore by Country path -->
        <div
          class="explore-routes-container"
          v-if="$route.name === 'countries' && this.route && !this.monument"
        >
          <div
            class="explore-routes"
            v-if="
              exploreData.locations && 
              exploreData.locations[0].relatedRoutes &&
              exploreData.locations[0].relatedRoutes.length > 0
            "
          >
            <div class="explore-routes-header">Suggested Routes</div>
            <div class="routes-select-container">
              <select v-model="routeId" @change="goTo($event, routeId)">
                <option v-if="this.route" selected disabled value="">
                  Select another Route
                </option>
                <option v-else selected disabled value="">
                  Select a Route
                </option>
                <!-- Sort Routes by "type" -->
                <optgroup label="Explore">
                  <option
                    v-for="route in exploreData.routes['EXPLORE']"
                    :key="returnValueInLanguage('en', route.routeName).value"
                    :value="route.routeId"
                  >
                    {{ returnValueInLanguage("en", route.routeName).value }}
                  </option>
                </optgroup>
                <optgroup label="Also Not To Be Missed">
                  <option
                    v-for="route in exploreData.routes['ALSO NOT TO BE MISSED']"
                    :key="returnValueInLanguage('en', route.routeName).value"
                    :value="route.routeId"
                  >
                    {{ returnValueInLanguage("en", route.routeName).value }}
                  </option>
                </optgroup>
                <optgroup label="To Know More">
                  <option
                    v-for="route in exploreData.routes['TO KNOW MORE']"
                    :key="returnValueInLanguage('en', route.routeName).value"
                    :value="route.routeId"
                  >
                    {{ returnValueInLanguage("en", route.routeName).value }}
                  </option>
                </optgroup>
              </select>
            </div>
          </div>
        </div>

        <!-- If arrived to Monument via Route path, include link to go back -->
        <!-- Only show if Route is present in current URL (can get to Monument via side navigation) -->
        <div v-if="fromRoute && $route.params.route" id="return-route-link">
          <a @click="goTo($event, $route.params.route.slice(2))">RETURN TO ROUTE</a>
        </div>

        <div id="explore-header">
          <span v-if="$route.name === 'itineraries'">
            <span v-if="country && !itinerary">Itineraries in </span>
            {{ exploreData.name }}
            <span v-if="subItinerary && !monument && exploreData.nameArray">
              <span
                v-if="
                  returnValueInLanguage('en', exploreData.nameArray).duration
                "
              >
                [{{
                  returnValueInLanguage("en", exploreData.nameArray).duration
                }}]
              </span>
            </span>
          </span>
          <span v-else-if="$route.params.route && !monument && exploreData.locations">
            <!-- Location is [0] because there will only be one Location per Route -->
            {{
              returnValueInLanguage("en", exploreData.locations[0].locationName)
                .value
            }}
            |
            {{ returnValueInLanguage("en", exploreData.nameArray).value }}
          </span>
          <span v-else>
            {{ exploreData.name }}
          </span>
          <span v-if="monument" id="monument-location">
            <span v-if="exploreData.locationName"
              >({{
                returnValueInLanguage("en", exploreData.locationName).value
              }},
            </span>
            <span v-if="exploreData.countryName"
              >{{
                returnValueInLanguage("en", exploreData.countryName).value
              }})</span
            >
          </span>
        </div>

        <!-- Wrapper for content that is NOT a Monument page and NOT Itinerary path -->
        <div
          id="explore-wrapper"
          v-if="!monument && !route && $route.name !== 'itineraries'"
        >
          <div id="extra-theme-info-wrapper">
            <!-- Show list of Territories when available -->
            <!-- Only on the Theme path, Country level -->
            <div id="territories-list" v-if="territoryList.length > 0">
              <span
                v-for="(territory, index) in territoryList"
                :key="territory + index"
              >
                {{ territory
                }}<span
                  v-if="
                    territoryList.length > 0 &&
                    index != territoryList.length - 1
                  "
                  >,
                </span>
              </span>
            </div>
            <!-- Only show Theme name for Themes which have one Country -->
            <!-- Take Theme name from breadcrumbs -->
            <div id="single-country-theme-name" v-if="singleCountry && breadcrumbs && breadcrumbs[0]">
              {{ breadcrumbs[0][0] }}
            </div>
          </div>

          <div id="explore-description">
            <div
              class="description-language-selection"
              v-if="descriptionLanguages.length > 1"
            >
              <label>Read in </label>
              <select v-model="descriptionLanguage">
                <option
                  v-for="language in descriptionLanguages"
                  :key="language"
                >
                  {{ language }}
                </option>
              </select>
            </div>
            <span v-html="exploreData.description"></span>
          </div>

          <!-- Tab menu for different text content -->
          <div id="explore-menu-container" v-if="!monument && showLocationMenu">
            <div id="explore-menu">
              <div
                id="explore-menu-background"
                class="explore-menu-item"
                :class="{ selected: view.background }"
                @click="changeView($event)"
                v-if="
                  exploreData.locationHistoricalBackground &&
                  exploreData.locationHistoricalBackground.length > 0 &&
                  returnValueInLanguage(
                    locationBackgroundLanguage,
                    exploreData.locationHistoricalBackground[0].details
                  ).description.length > 0
                "
              >
                HISTORICAL BACKGROUND
              </div>
              <div
                id="explore-menu-directions"
                class="explore-menu-item"
                :class="{ selected: view.directions }"
                @click="changeView($event)"
                v-if="
                  containsInfo('en', exploreData.details, [
                    'howToReach',
                    'contact',
                  ]) ||
                  containsInfo('en', exploreData.locationContact, [
                    'institution',
                    'street',
                    'zip',
                    'city',
                    'phone',
                    'fax',
                    'email',
                    'url',
                    'note',
                  ])
                "
              >
                GET DIRECTIONS
              </div>
              <div
                id="explore-menu-additional"
                class="explore-menu-item"
                :class="{ selected: view.additional }"
                @click="changeView($event)"
                v-if="
                  (exploreData.details &&
                    exploreData.details.length > 0 &&
                    returnValueInLanguage('en', this.exploreData.details) &&
                    returnValueInLanguage('en', exploreData.details).info &&
                    returnValueInLanguage('en', exploreData.details).info
                      .length > 0) ||
                  exploreData.accommodations.length > 0 ||
                  exploreData.guidedVisits.length > 0 ||
                  exploreData.usefulWebsites.length > 0
                "
              >
                ADDITIONAL INFORMATION
              </div>
            </div>
          </div>

          <div id="location-background-wrapper" v-if="view.background">
            <div id="location-background-container">
              <div
                id="location-background-label"
                v-if="
                  exploreData.locationHistoricalBackground &&
                  exploreData.locationHistoricalBackground.length > 0 &&
                  returnValueInLanguage(
                    locationBackgroundLanguage,
                    exploreData.locationHistoricalBackground[0].details
                  ).description.length > 0
                "
              >
                {{ exploreData.name }} | Historical Background
              </div>
              <div
                id="location-background-language-selection"
                class="language-selection"
                v-if="locationBackgroundLanguageOptions.length > 1"
              >
                <label>Read in </label>
                <select v-model="locationBackgroundLanguage">
                  <option
                    v-for="language in locationBackgroundLanguageOptions"
                    :key="language"
                  >
                    {{ language }}
                  </option>
                </select>
              </div>
              <DescriptionComponent
                v-if="locationBackgroundLanguage.length > 0"
                :language="locationBackgroundLanguage"
                :texts="exploreData.locationHistoricalBackground[0].details"
                :characters="descriptionChars"
              >
              </DescriptionComponent>
            </div>

            <div
              id="alt-location-background-container"
              v-if="altLocationBackgroundLanguage.length > 0"
            >
              <div
                id="alt-location-background-language-selection"
                class="language-selection"
                v-if="displayAltBackground"
              >
                <label>Read in </label>
                <select v-model="altLocationBackgroundLanguage">
                  <option
                    v-for="language in altLocationBackgroundLanguageOptions"
                    :key="language"
                  >
                    {{ language }}
                  </option>
                </select>
              </div>
              <div
                id="alt-location-background"
                v-if="displayAltBackground"
                v-html="
                  returnValueInLanguage(
                    altLocationBackgroundLanguage,
                    exploreData.locationHistoricalBackground[1].details
                  ).description
                "
              ></div>
              <a
                class="alt-location-background-link expand"
                v-if="!displayAltBackground"
                @click="displayAltBackground = !displayAltBackground"
              >
                Read detailed Historical Background.
              </a>
              <a
                class="alt-location-background-link collapse"
                v-else
                @click="displayAltBackground = !displayAltBackground"
              >
                Hide detailed Historical Background.
              </a>
            </div>
          </div>

          <div id="explore-directions-container" v-if="view.directions">
            <div id="explore-directions">
              <MonumentField
                :language="'en'"
                :details="exploreData.details"
                :field="'howToReach'"
                :label="'How to Reach'"
              >
              </MonumentField>
              <MonumentField
                :language="'en'"
                :details="exploreData.details"
                :field="'contact'"
                :label="'Contact'"
              >
              </MonumentField>
              <div
                id="other-contact"
                v-if="
                  this.$route.name === 'locations' &&
                  exploreData.locationContact.length > 0
                "
              >
                OTHER CONTACT
                <MonumentField
                  :language="'en'"
                  :details="exploreData.locationContact"
                  :field="'institution'"
                  :label="'Institution'"
                >
                </MonumentField>
                <MonumentField
                  :language="'en'"
                  :details="exploreData.locationContact"
                  :field="'street'"
                  :label="'Street'"
                >
                </MonumentField>
                <MonumentField
                  :language="'en'"
                  :details="exploreData.locationContact"
                  :field="'zip'"
                  :label="'Zip'"
                >
                </MonumentField>
                <MonumentField
                  :language="'en'"
                  :details="exploreData.locationContact"
                  :field="'city'"
                  :label="'City'"
                >
                </MonumentField>
                <MonumentField
                  :language="'en'"
                  :details="exploreData.locationContact"
                  :field="'phone'"
                  :label="'Phone'"
                >
                </MonumentField>
                <MonumentField
                  :language="'en'"
                  :details="exploreData.locationContact"
                  :field="'fax'"
                  :label="'Fax'"
                >
                </MonumentField>
                <MonumentField
                  :language="'en'"
                  :details="exploreData.locationContact"
                  :field="'email'"
                  :label="'Email'"
                >
                </MonumentField>
                <MonumentField
                  :language="'en'"
                  :details="exploreData.locationContact"
                  :field="'url'"
                  :label="'URL'"
                >
                </MonumentField>
                <MonumentField
                  :language="'en'"
                  :details="exploreData.locationContact"
                  :field="'note'"
                  :label="'Note'"
                >
                </MonumentField>
              </div>
            </div>
          </div>

          <div id="explore-additional-container" v-if="view.additional">
            <MonumentField
              v-if="exploreData.details.length > 0"
              :language="'en'"
              :details="exploreData.details"
              :field="'info'"
              :label="'Info'"
            >
            </MonumentField>
            <div
              class="explore-additional"
              v-if="exploreData.accommodations.length > 0"
            >
              <div class="additional-label">
                <div>ACCOMMODATIONS</div>
              </div>
              <div
                class="accommodations-wrapper"
                v-for="accommodation in exploreData.accommodations"
                :key="accommodation.title"
              >
                <div
                  class="accommodations-hotel"
                  v-for="hotel in accommodation.hotels"
                  :key="hotel.details.name"
                >
                  <div v-html="hotel.details.name"></div>
                  <div class="hotel-category" v-html="hotel.category"></div>
                  <div v-html="hotel.details.street"></div>
                  <div v-html="hotel.details.city"></div>
                  <div v-html="hotel.details.zip"></div>
                  <div v-html="hotel.details.phone"></div>
                  <div v-html="hotel.details.fax"></div>
                  <div v-html="hotel.details.email"></div>
                  <div class="hotel-url">
                    <a :href="hotel.details.url" target="_blank">
                      {{ hotel.details.url }}
                    </a>
                  </div>
                  <div v-html="hotel.details.note"></div>
                </div>
              </div>
            </div>
            <div
              class="explore-additional"
              v-if="exploreData.guidedVisits.length > 0"
            >
              <div class="additional-label">
                <div>GUIDED VISITS</div>
              </div>
              <div
                class="guided-visits-wrapper"
                v-for="visit in exploreData.guidedVisits"
                :key="visit.id"
              >
                <div class="guided-visits-contact">
                  <div v-html="visit.contact.name"></div>
                  <div v-html="visit.contact.street"></div>
                  <div v-html="visit.contact.city"></div>
                  <div v-html="visit.contact.zip"></div>
                  <div v-html="visit.contact.phone"></div>
                  <div v-html="visit.contact.fax"></div>
                  <div v-html="visit.contact.email"></div>
                  <div class="guided-visit-url">
                    <a :href="visit.contact.url" target="_blank">
                      {{ visit.contact.url }}
                    </a>
                  </div>
                  <div v-html="visit.contact.contactPerson"></div>
                  <div v-html="visit.contact.note"></div>
                </div>
              </div>
            </div>
            <div
              class="explore-additional"
              v-if="exploreData.usefulWebsites.length > 0"
            >
              <div class="additional-label">USEFUL WEBSITES</div>
              <div
                class="useful-websites-site"
                v-for="site in exploreData.usefulWebsites"
                :key="site.title"
              >
                <div>{{ site.title }}</div>
                <div class="useful-websites-url">
                  <a :href="site.link" target="_blank">
                    {{ site.link }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div id="explore-map-container">
            <div id="explore-map" ref="exploreMap"></div>
          </div>

          <!-- Route menu for Countries which have Routes -->
          <!-- For middle of page before a Route has been selected -->
          <div
            class="explore-routes-container"
            v-if="$route.name === 'countries'"
          >
            <div
              class="explore-routes"
              v-if="
                exploreData.relatedRoutes &&
                exploreData.relatedRoutes.length > 0
              "
            >
              <a id="suggested-routes" name="suggested-routes" class="explore-routes-header">Suggested Routes</a>
              <div id="explore-routes-description">
                A Route is a curated list of Monuments. If you wish to explore
                by Route, choose <span>Explore</span> to see just the
                highlights, <span>Also Not To Be Missed</span> for other
                important Monuments, and <span>To Know More</span> if you have
                extra time.
              </div>
              <div class="routes-select-container">
                <select v-model="routeId" @change="goTo($event, routeId)">
                  <option selected disabled value="">Select a Route</option>
                  <optgroup label="Explore">
                    <option
                      v-for="route in exploreData.routes['EXPLORE']"
                      :key="returnValueInLanguage('en', route.routeName).value"
                      :value="route.routeId"
                    >
                      {{ returnValueInLanguage("en", route.routeName).value }}
                    </option>
                  </optgroup>
                  <optgroup label="Also Not To Be Missed">
                    <option
                      v-for="route in exploreData.routes[
                        'ALSO NOT TO BE MISSED'
                      ]"
                      :key="returnValueInLanguage('en', route.routeName).value"
                      :value="route.routeId"
                    >
                      {{ returnValueInLanguage("en", route.routeName).value }}
                    </option>
                  </optgroup>
                  <optgroup label="To Know More">
                    <option
                      v-for="route in exploreData.routes['TO KNOW MORE']"
                      :key="returnValueInLanguage('en', route.routeName).value"
                      :value="route.routeId"
                    >
                      {{ returnValueInLanguage("en", route.routeName).value }}
                    </option>
                  </optgroup>
                </select>
              </div>
            </div>
          </div>

          <!-- Page navigation for Monuments only -->
          <div
            id="page-navigation"
            class="page-navigation-wrapper"
            v-if="displayPageNavigation"
          >
            <PagesComponent
              :pageInfo="pageInfo"
              :navigatePage="navigatePage"
              :navigatePageDirect="navigatePageDirect"
            ></PagesComponent>
          </div>

          <!-- Display next "level" of data in tile form -->
          <div id="explore-next-container">
            <div class="explore-next-header">
              Explore by
              <span v-if="location">Monument</span>
              <span v-else-if="country || territory">Location</span>
              <span v-else>Country</span>
            </div>
            <div class="explore-next-tiles">
              <div
                class="explore-next-tile"
                :class="{
                  'country-tiles': displayCountries,
                  'map-tiles': !displayCountries,
                  'monument-tiles': !displayCountries && displayMonuments,
                }"
                v-for="item in exploreNext"
                :key="item[Object.keys(item)[0]]"
                :style="tileBackground(item)"
              >
                <!-- Create correct URL for each tile by passing in parameters -->
                <!-- Links for exploring by Theme (different route)  -->
                <router-link
                  class="explore-next-link"
                  v-if="theme"
                  :to="{
                    name: 'themes',
                    params: {
                      theme: theme,
                      country: `c-${item.countryId}`,
                      territory: item.territoryId
                        ? `tr-${item.territoryId}`
                        : $route.params.territory
                        ? $route.params.territory
                        : item.territoryId,
                      location: item.locationId
                        ? `l-${item.locationId}`
                        : item.locationId,
                      monument: item.monumentId
                        ? `m-${item.monumentId}`
                        : item.monumentId,
                    },
                  }"
                >
                </router-link>
                <!-- Links for exploring by Country -->
                <router-link
                  class="explore-next-link"
                  v-else
                  :to="{
                    name: 'countries',
                    params: {
                      country: country,
                      filter: item.filterId
                        ? `f-${item.filterId}`
                        : $route.params.filter
                        ? $route.params.filter
                        : item.filterId,
                      territory: item.territoryId
                        ? `tr-${item.territoryId}`
                        : $route.params.territory
                        ? $route.params.territory
                        : item.territoryId,
                      location: item.locationId
                        ? `l-${item.locationId}`
                        : item.locationId,
                      monument: item.monumentId
                        ? `m-${item.monumentId}`
                        : item.monumentId,
                    },
                  }"
                >
                </router-link>
                <div
                  v-if="!displayCountries && displayMonuments"
                  class="monument-next-label"
                >
                  <!-- Links for exploring by Theme -->
                  <router-link
                    v-if="theme"
                    :to="{
                      name: 'themes',
                      params: {
                        theme: theme,
                        country: `c-${item.countryId}`,
                        territory: item.territoryId
                          ? `tr-${item.territoryId}`
                          : $route.params.territory
                          ? $route.params.territory
                          : item.territoryId,
                        location: item.locationId
                          ? `l-${item.locationId}`
                          : item.locationId,
                        monument: item.monumentId
                          ? `m-${item.monumentId}`
                          : item.monumentId,
                      },
                    }"
                    v-html="truncate(65, item.name)"
                  >
                  </router-link>
                  <!-- Links for exploring by Country -->
                  <router-link
                    v-else
                    :to="{
                      name: 'countries',
                      params: {
                        country: country,
                        filter: item.filterId
                        ? `f-${item.filterId}`
                        : $route.params.filter
                        ? $route.params.filter
                        : item.filterId,
                        territory: item.territoryId
                          ? `tr-${item.territoryId}`
                          : $route.params.territory
                          ? $route.params.territory
                          : item.territoryId,
                        location: item.locationId
                          ? `l-${item.locationId}`
                          : item.locationId,
                        monument: item.monumentId
                          ? `m-${item.monumentId}`
                          : item.monumentId,
                      },
                    }"
                    v-html="truncate(65, item.name)"
                  >
                  </router-link>
                </div>
                <span v-else class="explore-next-label" v-html="item.name">
                </span>
              </div>
            </div>

            <!-- Repeat page controls for easier navigation -->
            <div
              id="page-navigation"
              class="page-navigation-wrapper"
              v-if="displayPageNavigation"
            >
              <PagesComponent
                :pageInfo="pageInfo"
                :navigatePage="navigatePage"
                :navigatePageDirect="navigatePageDirect"
              ></PagesComponent>
            </div>
          </div>

          <BooksTours
            :books="exploreData.mwnfTravelBook"
            :tours="exploreData.mwnfTour"
          >
          </BooksTours>
        </div>

        <!-- The component is Sub-Itinerary because Routes and Sub-Itineraries are the same in the API -->
        <!-- Reuse Sub-Itinerary template -->
        <div
          id="routes-wrapper"
          v-else-if="route && !monument && $route.name !== 'itineraries'"
        >
          <SubItinerary
            :current="exploreData"
            :next="exploreNext"
            @location-change="updateLocation"
          >
          </SubItinerary>
        </div>

        <div
          id="itineraries-wrapper"
          v-else-if="$route.name === 'itineraries' && !monument"
        >
          <div
            v-if="!subItinerary || (subItinerary && monument)"
            id="explore-map-container"
          >
            <div id="explore-map" ref="exploreMap"></div>
          </div>

          <ItinerariesComponent
            :level="level"
            :current="exploreData"
            :next="exploreNext"
            :index="subItineraryLocationIndex"
            :locationId="subItineraryLocationId"
            @location-change="updateLocation"
          >
          </ItinerariesComponent>
        </div>

        <!-- Monument component shows when it is a Monument page (different layout) -->
        <MonumentComponent v-else :monument="exploreData"></MonumentComponent>
      </div>
    </div>
  </div>
</template>

<script>
import LoaderComponent from "../components/LoaderComponent.vue";
import DescriptionComponent from "../components/DescriptionComponent.vue";
import BooksTours from "../components/BooksTours.vue";
import PagesComponent from "../components/PagesComponent.vue";
import pageDisplay from "../mixins/pageDisplay.js";
import ItinerariesComponent from "../components/ItinerariesComponent.vue";
import SubItinerary from "../components/SubItinerary.vue";
import MonumentField from "../components/MonumentField.vue";
import SideNavigation from "../components/SideNavigation.vue";
import MonumentComponent from "../components/MonumentComponent.vue";
import language from "../mixins/language.js";
import truncate from "../mixins/truncate.js";
export default {
  name: "ExplorePage",
  components: {
    LoaderComponent: LoaderComponent,
    SideNavigation: SideNavigation,
    ItinerariesComponent: ItinerariesComponent,
    SubItinerary: SubItinerary,
    DescriptionComponent: DescriptionComponent,
    BooksTours: BooksTours,
    MonumentComponent: MonumentComponent,
    MonumentField: MonumentField,
    PagesComponent: PagesComponent,
  },
  props: [
    "theme",
    "country",
    "filter",
    "territory",
    "itinerary",
    "subItinerary",
    "location",
    "route",
    "monument",
  ],
  mixins: [language, pageDisplay, truncate],
  data() {
    return {
      loaded: false,

      descriptionLanguage: "",
      descriptionLanguages: [],

      currentLanguage: "en",
      locationBackgroundLanguage: "",
      locationBackgroundLanguageOptions: [],
      displayAltBackground: false,
      altLocationBackgroundLanguage: "",
      altLocationBackgroundLanguageOptions: [],
      descriptionChars: 575,

      view: {
        background: true,
        directions: false,
        additional: false,
      },

      displayPageNavigation: false,
      pageInfo: {},
      pages: {},

      // Placeholder data
      mapCenterZoom: 4,
      mapCenterCoordinates: {
        lat: 36.728804,
        lng: 3.101106,
      },

      level: "",
      builtURL: "",
      subItineraryURL: "",
      currentLocation: null,
      currentLocationKey: null,
      last: false,

      current: {
        theme: null,
        country: null,
        filter: null,
        territory: null,
        itinerary: null,
        subItinerary: null,
        location: null,
        monument: null,
      },

      exploreData: {},
      exploreNext: [],

      territoryList: [],
      singleCountry: false,

      routeId: "",
      fromRoute: this.$route.params.route && this.monument ? true : false,

      subItineraryLocationIndex: null,
      subItineraryLocationId: null,

      showLocationMenu: false,
    };
  },
  computed: {
    displayCountries: function () {
      if (this.theme) {
        if (
          !this.monument &&
          !this.location &&
          !this.territory &&
          !this.filter &&
          !this.country
        ) {
          return true;
        }
      }
      return false;
    },
    displayMonuments: function () {
      if (this.location) {
        return true;
      } else {
        return false;
      }
    },
    // Get current "path" and create URLs for breadcrumb links
    // Number of links will always match URL parameters (no need to validate)
    breadcrumbs: function () {
      let filtered = Object.values(this.current).filter(function (crumb) {
        return crumb !== null;
      });
      let array = [];
      let path = this.$route.path.substring(1).split("/");
      for (let i = 0; i < filtered.length; i++) {
        let slice = path.slice(0, i + 2);
        let joined = slice.join("/");
        array.push([filtered[i], joined]);
      }
      // If on Sub-Itinerary Monument page, change Location URL
      // (Remove Monument section of path)
      if (this.$route.name === "itineraries" && this.monument) {
        if (array.length >= 5) {
          let string = array[array.length - 2][1];
          let end = string.lastIndexOf("/");
          array[array.length - 2][1] = string.substr(0, end);
        }
      }
      return array;
    },
    // Locations are handled differently for Itineraries (no self-page)
    locationRoute: function () {
      let obj = {
        name: "itineraries",
        params: {},
      };
      let path = this.$route.path.substring(1).split("/");
      path.shift();
      obj.params.country = path[0];
      if (path[1].includes("tr-")) {
        obj.params.territory = path[1];
        obj.params.itinerary = path[2];
        obj.params.subItinerary = path[3];
      } else {
        obj.params.itinerary = path[1];
        obj.params.subItinerary = path[2];
      }
      return obj;
    },
  },
  watch: {
    $route(to, from) {
      if (to.hash && !from.params.monument) {
        const element = document.getElementById(to.hash.replace(/#/, ""));
        element.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } else {
        this.loaded = false;
        // Check for Route (shows Route link if true)
        if (from.params.route && to.params.monument) {
          this.fromRoute = true;
        } else {
          this.fromRoute = false;
        }
        this.view.background = true;
        this.view.directions = false;
        this.view.additional = false;
        this.getData();
      }
    },
    // Get/set Description language (Itinerary is different)
    descriptionLanguage() {
      if (this.$route.name === "itineraries") {
        if (
          this.exploreData.descriptionArray &&
          this.returnValueInLanguage(
            this.descriptionLanguage,
            this.exploreData.descriptionArray
          ).description.length > 0
        ) {
          this.exploreData["description"] = this.returnValueInLanguage(
            this.descriptionLanguage,
            this.exploreData.descriptionArray
          ).description;
        } else {
          this.exploreData["description"] = "";
        }
      } else {
        if (this.exploreData.descriptionArray &&
          this.returnValueInLanguage(
            this.descriptionLanguage,
            this.exploreData.descriptionArray).value.length > 0) {
          this.exploreData["description"] = this.returnValueInLanguage(
            this.descriptionLanguage,
            this.exploreData.descriptionArray).value;
        } else {
          this.exploreData["description"] = "";
        }
      }
    },
    exploreData() {
      this.displayLocationMenu();
    },
  },
  methods: {
    // Determine if the Location menu needs to be shown
    displayLocationMenu: function () {
      this.showLocationMenu = false;
      if (
        (this.exploreData.locationHistoricalBackground &&
          this.exploreData.locationHistoricalBackground.length > 0 &&
          this.returnValueInLanguage(
            this.locationBackgroundLanguage,
            this.exploreData.locationHistoricalBackground[0].details
          ).description.length > 0) ||
        (this.exploreData.details &&
          (this.containsInfo("en", this.exploreData.details, [
            "howToReach",
            "contact",
          ]) ||
            this.containsInfo("en", this.exploreData.locationContact, [
              "institution",
              "street",
              "zip",
              "city",
              "phone",
              "fax",
              "email",
              "url",
              "note",
            ]))) ||
        (this.exploreData.details &&
          this.exploreData.details.length > 0 &&
          this.returnValueInLanguage("en", this.exploreData.details) &&
          this.returnValueInLanguage("en", this.exploreData.details).info &&
          this.returnValueInLanguage("en", this.exploreData.details).info
            .length > 0) ||
        this.exploreData.accommodations.length > 0 ||
        this.exploreData.guidedVisits.length > 0 ||
        this.exploreData.usefulWebsites.length > 0
      ) {
        this.showLocationMenu = true;
      } else {
        this.showLocationMenu = false;
      }
    },
    locationBreadcrumb: function (array, index) {
      // If on the Itinerary path
      // Location breadcrumb's link needs to be different
      // (Links to Sub-Itinerary page)
      if (this.$route.name === "itineraries") {
        if (array.length >= 5 && this.monument) {
          if (index === array.length - 2) {
            return true;
          }
        }
      }
      return false;
    },
    // Tab menu handler
    changeView: function (e) {
      for (let category in this.view) {
        this.view[category] = false;
      }
      switch (e.target.id) {
        case "explore-menu-background":
          this.view.background = true;
          break;
        case "explore-menu-directions":
          this.view.directions = true;
          break;
        case "explore-menu-additional":
          this.view.additional = true;
          break;
      }
    },
    // Check if there is data in a prop
    containsInfo: function (language, details, props) {
      if (details) {
        if (details.length > 0) {
          let object = this.returnValueInLanguage(language, details);
          let found = false;
          if (!object) {
            return false;
          }
          for (let i = 0; i < props.length; i++) {
            if (object[props[i]]) {
              if (object[props[i]].length > 0) {
                return (found = true);
              }
            }
          }
          if (found) {
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
    // Set attribute for tile (image or solid color)
    tileBackground: function (item) {
      if (
        !this.displayCountries &&
        this.displayMonuments &&
        item.monumentImages
      ) {
        if (item.monumentImages.length > 0) {
          return { backgroundImage: `url(${item.monumentImages[0].url})` };
        }
      } else if (!this.displayCountries && item.locationImages) {
        if (item.locationImages.length > 0) {
          return { backgroundImage: `url(${item.locationImages[0].url}` };
        }
      } else if (this.displayCountries && item.countryImage) {
        if (item.countryImage.length > 0) {
          return { backgroundImage: `url(${item.countryImage})` };
        }
      } else {
        return { backgroundColor: "#006950" };
      }
    },
    goTo: function (e, id) {
      let obj = {};
      obj.name = "countries";
      obj.params = {
        country: this.country,
        filter: this.filter,
        territory: this.territory,
        location: this.location,
        route: `r-${id}`,
      };
      this.$router.push(obj);
    },
    navigatePage: function (page) {
      let url;
      if (page === "first") {
        url = this.pages.first;
      } else if (page === "last") {
        url = this.pages.last;
      } else if (page === "next") {
        url = this.pages.next;
      } else if (page === "previous") {
        url = this.pages.prev;
      }
      return axios({ method: "GET", url: url }).then(
        (result) => {
          this.processNextData(result.data.data);

          this.pageInfo = result.data.meta;
          this.pages = result.data.links;
        },
        (error) => {
          console.error(error);
        }
      );
    },
    // Navigate directly to a page by clicking a number
    navigatePageDirect: function (page) {
      let length = this.pages.first.length;
      let base = this.pages.first.slice(0, length - 1);
      let url = base + page;

      return axios({ method: "GET", url: url }).then(
        (result) => {
          this.processNextData(result.data.data);

          this.pageInfo = result.data.meta;
          this.pages = result.data.links;
        },
        (error) => {
          console.error(error);
        }
      );
    },
    // Set current according to current "path"
    // (Handles direct navigation)
    loadCurrent: function (obj) {
      this.current.theme = obj.theme;
      this.current.country = obj.country;
      this.current.filter = obj.filter;
      this.current.territory = obj.territory;
      this.current.itinerary = obj.itinerary;
      this.current.subItinerary = obj.subItinerary;
      this.current.location = obj.location;
      this.current.monument = obj.monument;
    },
    // Set current according to selected dropdown
    setCurrent: function (obj) {
      // Selecting a "higher" level will clear/reset "lower" levels
      switch (obj.category) {
        case "theme":
          this.current.theme = obj.name;
          this.current.country = null;
          this.current.territory = null;
          // Not sure the following two are necessary
          this.current.itinerary = null;
          this.current.subItinerary = null;
          this.current.location = null;
          this.current.monument = null;
          break;
        case "country":
          this.current.country = obj.name;
          this.current.territory = null;
          this.current.itinerary = null;
          this.current.subItinerary = null;
          this.current.location = null;
          this.current.monument = null;
          this.routeId = "";
          break;
        case "filter":
          this.current.filter = obj.name;
          this.current.territory = null;
          this.current.itinerary = null;
          this.current.subItinerary = null;
          this.current.location = null;
          this.current.monument = null;
          this.routeId = "";
          break;
        case "territory":
          this.current.territory = obj.name;
          this.current.itinerary = null;
          this.current.subItinerary = null;
          this.current.location = null;
          this.current.monument = null;
          this.routeId = "";
          break;
        case "itinerary":
          this.current.itinerary = obj.name;
          this.current.subItinerary = null;
          break;
        case "subItinerary":
          this.current.subItinerary = obj.name;
          break;
        case "location":
          this.current.location = obj.name;
          this.current.monument = null;
          this.routeId = "";
          break;
        case "monument":
          this.current.monument = obj.name;
          break;
        default:
          break;
      }
    },
    // Change Location based on index
    setLocation: function (index) {
      this.subItineraryLocationIndex = index;
    },
    saveLocation: function (id) {
      this.subItineraryLocationId = id;
    },
    // Remove text identifiers, leaving only the bare ID
    strip: function (str) {
      if (str.includes("tr-") || str.includes("si-")) {
        return str.substring(3);
      } else {
        return str.substring(2);
      }
    },
    // Create API URLs for getting correct information
    buildURL: function () {
      // Split the URL path, reverse (for the for loop below)
      // ex. themes/t-1/c-dz/l-241 = ["l-241", "c-dz", "t-1", "themes"]
      let path = this.$route.path.substring(1).split("/");
      path = path.reverse();
      // Pop off the route ("themes" or "countries")
      path.pop();
      // Set according to text identifiers (necessary to differentiate for API filters)
      // ex. "te-" indicates Territory ID
      this.builtURL = "";
      let base = `${process.env.VUE_APP_API_ENDPOINT}/`;
      let b;
      // this.level is for passing to Itineraries component to determine display of data
      if (this.monument) {
        b = `monuments/${this.strip(this.monument)}?`;
        this.last = true;
      } else if (this.route) {
        b = `itineraries/sub-itineraries/${this.strip(this.route)}?`;
        this.last = false;
      } else if (this.location) {
        b = `locations/${this.strip(this.location)}?`;
        this.last = false;
      } else if (this.subItinerary) {
        this.level = "sub-itinerary";
        b = `itineraries/sub-itineraries/${this.strip(this.subItinerary)}?`;
        this.last = false;
      } else if (this.itinerary) {
        this.level = "itinerary";
        b = `itineraries/itineraries/${this.strip(this.itinerary)}?`;
        this.last = false;
      } else if (this.territory) {
        if (this.$route.name === "itineraries") {
          this.level = "territory";
          b = `itineraries/territories/${this.strip(this.territory)}?`;
        } else {
          b = `territories/${this.strip(this.territory)}?`;
        }
        this.last = false;
      } else if (this.country) {
        if (this.$route.name === "itineraries") {
          this.level = "country";
          b = `itineraries/countries/${this.strip(this.country)}?`;
        } else {
          b = `countries/${this.strip(this.country)}?`;
        }
        this.last = false;
      } else if (this.theme) {
        b = `themes/${this.strip(this.theme)}?`;
        this.last = false;
      }
      let url = base + b;
      if (this.theme) {
        if (this.country) {
          url += `&t=${this.strip(this.theme)}`;
        }
      }
      if (this.territory) {
        url += `&tr=${this.strip(this.territory)}`;
      }
      if (this.filter) {
        url += `&f=${this.strip(this.filter)}`;
      }
      this.builtURL = url;
    },
    getData: function () {
      this.displayPageNavigation = false;
      this.buildURL();

      console.log("dataURL: ", this.builtURL);

      axios({ method: "GET", url: this.builtURL }).then(
        (result) => {
          this.loaded = true;
          // Check to see if page is invalid
          // If so, return and show error page
          if (result.data === "") {
            return this.$router.push({ name: "error" });
          }
          this.exploreData = result.data;
          let results = result.data;
          // Set (add keys to object) name, description, and ID as appropriate
          // Purpose: for display, refer to keys which will be the same between different categories
          // Otherwise: "cycleName" vs "countryName" instead of "name"
          let keys = Object.keys(results);
          let next, isMonuments;
          let categories = [
            "monument",
            "location",
            "subItinerary",
            "itinerary",
            "territory",
            "filter",
            "country",
            "theme",
          ];
          for (let i = 0; i < categories.length; i++) {
            // If the first key in results doesn't include the current category, continue
            if (!keys[0].includes(categories[i])) {
              continue;
            } else {
              // Sub-Itinerary name needs to have the Roman numeral added in front of it
              if (categories[i] === "subItinerary") {
                this.exploreData["name"] = `${
                  results.subItineraryOrderRoman
                }. ${
                  this.returnValueInLanguage(
                    "en",
                    results[`${categories[i]}Name`]
                  ).value
                }`;
              } else {
                this.exploreData["name"] = this.returnValueInLanguage(
                  "en",
                  results[`${categories[i]}Name`]
                ).value;
              }
              this.exploreData["nameArray"] = results[`${categories[i]}Name`];
              // Reset descriptionLanguages array
              this.descriptionLanguages = [];
              // Save Description if available
              if (results[`${categories[i]}Description`]) {
                if (results[`${categories[i]}Description`].length > 0) {
                  this.getLanguageList(
                    results[`${categories[i]}Description`],
                    "descriptionLanguages"
                  );
                  this.setLanguage(
                    this.descriptionLanguages,
                    "descriptionLanguage"
                  );
                  if (
                    categories[i] !== "subItinerary" &&
                    categories[i] !== "itinerary"
                  ) {
                    this.exploreData["description"] =
                      this.returnValueInLanguage(
                        this.descriptionLanguage,
                        results[`${categories[i]}Description`]
                      ).value;
                    this.exploreData["descriptionArray"] =
                      results[`${categories[i]}Description`];
                  }
                }
                // If no Description is available, empty array
              } else {
                this.exploreData["description"] = "";
                this.exploreData["descriptionArray"] = [];
              }
              if (results[`${categories[i]}Details`]) {
                this.exploreData["details"] =
                  results[`${categories[i]}Details`];
              }
              // Save Accommodation, Guided Visits, and Useful Websites information
              let saveInfo = (type) => {
                if (results[type] && results[type].length > 0) {
                  this.exploreData[type] = results[type];
                }
              };
              saveInfo("accommodations");
              this.enAccommodations(this.exploreData.accommodations);
              saveInfo("guidedVisits");
              this.enGuidedVisits(this.exploreData.guidedVisits);
              saveInfo("usefulWebsites");
              this.enUsefulWebsites(this.exploreData.usefulWebsites);

              if (categories[i] === "monument") {
                this.exploreData["thisId"] = `m-${results.monumentId}`;
              } else if (categories[i] === "location") {
                this.exploreData["thisId"] = `l-${results.locationId}`;
              } else if (categories[i] === "subItinerary") {
                this.exploreData["thisId"] = `si-${results.subItineraryId}`;
              } else if (categories[i] === "itinerary") {
                this.exploreData["thisId"] = `i-${results.itineraryId}`;
              } else if (categories[i] === "territory") {
                this.exploreData["thisId"] = `tr-${results.territoryId}`;
              } else if (categories[i] === "filter") {
                this.exploreData["thisId"] = `f-${results.filterId}`;
              } else if (categories[i] === "country") {
                this.exploreData["thisId"] = `c-${results.countryId}`;
              }
            }
          }

          // Reset/empty territoryList
          this.territoryList = [];
          // Only get list of Territories for display on Theme path, Country level
          if (this.$route.name === "themes" && this.country && !this.location) {
            this.getTerritories(results.links.territories);
          }
          // Check if Theme has a single Country or not
          if (this.$route.name === "themes" && !this.location) {
            this.checkSingleCountry(this.strip(this.theme));
          }

          // Set page title (shown in tab/window)
          let title = this.exploreData.name;
          if (this.$route.params.route && !this.monument) {
            title = this.returnValueInLanguage(
              "en",
              this.exploreData.locations[0].locationName
            ).value;
          }
          document.title = `${title} | Explore with MWNF`;

          if (
            this.exploreData.locationHistoricalBackground &&
            this.exploreData.locationHistoricalBackground.length > 0
          ) {
            if (this.exploreData.locationHistoricalBackground.length > 1) {
              this.getLanguages(
                this.exploreData.locationHistoricalBackground[1].details,
                "altLocationBackgroundLanguage",
                "altLocationBackgroundLanguageOptions"
              );
            }
            this.getLanguages(
              this.exploreData.locationHistoricalBackground[0].details,
              "locationBackgroundLanguage",
              "locationBackgroundLanguageOptions"
            );
          } else {
            this.locationBackgroundLanguage = "";
            this.locationBackgroundLanguageOptions = [];
            this.altLocationBackgroundLanguage = "";
            this.altLocationBackgroundLanguageOptions = [];
          }

          for (let i = 0; i < keys.length; i++) {
            if (keys[i] === "monuments") {
              next = results.links.monuments;
              if (this.filter) {
                next += `&f=${this.strip(this.filter)}`;
              }
              isMonuments = true;
              break;
              // Otherwise, save returned array
            } else if (
              keys[i] === "countries" ||
              keys[i] === "itineraries" ||
              keys[i] === "subItineraries" ||
              keys[i] === "locations"
            ) {
              next = results[keys[i]];
              isMonuments = false;
              break;
            }
          }

          // Handle if Route information is available
          if ("relatedRoutes" in results ||
            (results.locations && "relatedRoutes" in results.locations[0])
          ) {
            if (
              (results.relatedRoutes && results.relatedRoutes.length > 0) ||
              (results.locations &&
                results.locations[0].relatedRoutes &&
                results.locations[0].relatedRoutes.length > 0)
            ) {
              let newRoutes = {};

              let routes;
              if (results.relatedRoutes) {
                routes = results.relatedRoutes;
              } else if (results.locations[0].relatedRoutes) {
                routes = results.locations[0].relatedRoutes;
              }
              let types = [];
              for (let i = 0; i < routes.length; i++) {
                types.push(
                  this.returnValueInLanguage("en", routes[i].routeType).value
                );
              }
              let reduced = [...new Set(types)];

              for (let i = 0; i < reduced.length; i++) {
                let group = [];
                for (let j = 0; j < routes.length; j++) {
                  let type = this.returnValueInLanguage(
                    "en",
                    routes[j].routeType
                  ).value;
                  if (reduced[i] === type) {
                    group.push(routes[j]);
                  }
                }
                group.sort(function (a, b) {
                  let orderA = a.routeOrder;
                  let orderB = b.routeOrder;
                  return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
                });
                newRoutes[reduced[i]] = group;
              }
              this.exploreData.routes = newRoutes;
            }
          }

          this.setMapData(this.exploreData);

          // If "last" flag is false, wait to generate map until after next data is received
          // Else, generate the map with the current data (Monument level)
          if (!this.last) {
            this.getNextData(next, isMonuments);
          } else {
            this.currentLocation = this.exploreData.locationId;
            this.$nextTick(() => {
              this.generateMap(this.createMapArray(this.exploreData));
            });
          }
        },
        (error) => {
          console.error(error);
        }
      );
    },
    // Get additional information in English
    enAccommodations: function (data) {
      let array = [];
      for (let acc in data) {
        let obj = {};
        obj.hotels = [];
        let hotel = {};
        hotel.category = this.returnValueInLanguage(
          "en",
          data[acc].hotels.category
        )["type_name"];
        hotel.details = this.returnValueInLanguage(
          "en",
          data[acc].hotels.details
        );
        obj.hotels.push(hotel);
        array.push(obj);
      }
      this.exploreData.accommodations = array;
    },
    enGuidedVisits: function (data) {
      let array = [];
      for (let visit in data) {
        let obj = {};
        obj.id = data[visit].contactId;
        obj.contact = this.returnValueInLanguage("en", data[visit].details);
        array.push(obj);
      }
      this.exploreData.guidedVisits = array;
    },
    enUsefulWebsites: function (data) {
      let array = [];
      for (let site in data) {
        let obj = {};
        obj.title = this.returnValueInLanguage("en", data[site].details).title;
        obj.link = this.returnValueInLanguage("en", data[site].details).link;
        array.push(obj);
      }
      this.exploreData.usefulWebsites = array;
    },
    // Get Territory list for displaying on Explore by Theme path
    getTerritories: function (url) {
      axios({ method: "GET", url: url }).then(
        (result) => {
          let territoryNames = [];
          for (let i = 0; i < result.data.length; i++) {
            territoryNames.push(
              this.returnValueInLanguage("en", result.data[i].territoryName)
                .value
            );
          }
          const collator = new Intl.Collator("en", {
            numeric: true,
            sensitivity: "base",
          });
          territoryNames.sort((a, b) => {
            return collator.compare(a, b);
          });
          this.territoryList = territoryNames;
        },
        (error) => {
          console.error(error);
        }
      );
    },
    checkSingleCountry: function (theme) {
      this.singleCountry = false;
      axios({
        method: "GET",
        url: `${process.env.VUE_APP_API_ENDPOINT}/themes/${theme}`,
      }).then(
        (result) => {
          if (result.data.countries.length > 1) {
            this.singleCountry = false;
          } else {
            this.singleCountry = true;
          }
        },
        (error) => {
          console.error(error);
        }
      );
    },
    getNextData: function (next, get) {
      if (!get) {
        this.processNextData(next);
      } else {
        return axios({ method: "GET", url: next }).then(
          (result) => {
            this.processNextData(result.data.data);
            // Set pagination display if necessary
            if (result.data.meta["last_page"] > 1) {
              this.pageInfo = result.data.meta;
              this.pages = result.data.links;
              this.displayPageNavigation = true;
            }
          },
          (error) => {
            console.error(error);
          }
        );
      }
    },
    // Handle next "level" of data (for tile display)
    processNextData: function (next) {
      this.exploreNext = next;
      let results = next;
      for (let i = 0; i < results.length; i++) {
        let keys = Object.keys(results[i]);
        let nameSet = false,
          idSet = false;
        for (let j = 0; j < keys.length; j++) {
          if (this.$route.name !== "itineraries" && nameSet && idSet) {
            break;
          }
          if (!nameSet) {
            if (keys[j].includes("Name")) {
              if (keys[j].includes("subItinerary")) {
                this.exploreNext[i]["name"] = `${
                  results[i].subItineraryOrderRoman
                }. ${
                  this.returnValueInLanguage("en", results[i][keys[j]]).value
                }`;
              } else {
                this.exploreNext[i]["name"] = this.returnValueInLanguage(
                  "en",
                  results[i][keys[j]]
                ).value;
              }
              this.exploreNext[i]["nameArray"] = results[i][keys[j]];
              nameSet = true;
            }
          }
          if (!idSet) {
            if (keys[j].includes("Id")) {
              if (keys[j].includes("monument")) {
                this.exploreNext[i]["thisId"] = `m-${results[i][keys[j]]}`;
              } else if (keys[j].includes("location")) {
                this.exploreNext[i]["thisId"] = `l-${results[i][keys[j]]}`;
              } else if (keys[j].includes("subItinerary")) {
                this.exploreNext[i]["thisId"] = `si-${results[i][keys[j]]}`;
              } else if (keys[j].includes("itinerary")) {
                this.exploreNext[i]["thisId"] = `i-${results[i][keys[j]]}`;
              } else if (keys[j].includes("territory")) {
                this.exploreNext[i]["thisId"] = `tr-${results[i][keys[j]]}`;
              } else if (keys[j].includes("filter")) {
                this.exploreNext[i]["thisId"] = `f-${results[i][keys[j]]}`;
              } else if (keys[j].includes("country")) {
                this.exploreNext[i]["thisId"] = `c-${results[i][keys[j]]}`;
              }
              idSet = true;
            }
          }
          if (this.$route.name === "itineraries") {
            if (keys[j].includes("Order")) {
              if (keys[j].includes("Roman")) {
                continue;
              } else {
                this.exploreNext[i]["order"] = results[i][keys[j]];
              }
            }
            if (keys[j].includes("Description")) {
              if (results[i][keys[j]]) {
                if (
                  !keys[j] === "itineraryDescription" ||
                  !keys[j] === "subItineraryDescription"
                ) {
                  this.exploreNext[i]["description"] =
                    this.returnValueInLanguage(
                      "en",
                      results[i][keys[j]]
                    ).description;
                  this.exploreNext[i]["descriptionArray"] = results[i][keys[j]];
                }
              }
            }
          }
        }
      }
      // Sort Location by order
      // Not sorted in Sub-Itinerary component because Location selection
      // emits back up to this level to re-generate map
      if (this.subItinerary && !this.monument) {
        this.exploreNext.sort(function (a, b) {
          let orderA = a.order;
          let orderB = b.order;
          return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
        });
      } else {
        const collator = new Intl.Collator("en", {
          numeric: true,
          sensitivity: "base",
        });
        this.exploreNext.sort((a, b) => {
          let nameA = a.name;
          let nameB = b.name;
          return collator.compare(nameA, nameB);
        });
      }
      if ((this.subItinerary && !this.monument) || this.route) {
        // exploreNext is set to Location within Sub-Itinerary at this level
        // That's how we know [0] is the only option
        this.$nextTick(() => {
          this.generateMap(this.createMapArray(this.exploreNext[0].monuments));
        });
      } else {
        this.generateMap(this.createMapArray(this.exploreNext));
      }
    },
    updateLocation: function (index) {
      this.generateMap(this.createMapArray(this.exploreNext[index].monuments));
      this.setLocation(index);
    },
    createMapArray: function (data) {
      console.log(data);
      let mapArray = [];

      let info = {};
      if (Array.isArray(data)) {
        for (let i = 0; i < data.length; i++) {
          if (data[i].geoCoordinates) {
            info = {};
            info.latitude = data[i].geoCoordinates.latitude;
            info.longitude = data[i].geoCoordinates.longitude;
            if (
              (this.subItinerary && !this.monument) ||
              (this.route && !this.monument)
            ) {
              info.name = this.returnValueInLanguage(
                "en",
                data[i].monumentName
              ).value;
              info.id = `m-${data[i].monumentId}`;
              // exploreNext[0] is the only option at this level to get Location name
              info.location = this.exploreNext[0].name;
            } else {
              info.name = data[i].name;
              info.id = data[i].thisId;
            }
            mapArray.push(info);
          } else {
            continue;
          }
        }
      } else {
        if (data.geoCoordinates) {
          info.latitude = data.geoCoordinates.latitude;
          info.longitude = data.geoCoordinates.longitude;
          info.name = data.name;
          info.id = data.thisId;
          mapArray.push(info);
        }
      }

      return mapArray;
    },
    setMapData: function (data) {
      this.mapCenterZoom = Number(data.geoCoordinates.zoom);
      this.mapCenterCoordinates.lat = data.geoCoordinates.latitude;
      this.mapCenterCoordinates.lng = data.geoCoordinates.longitude;
    },
    generateMap: function (array) {
      // Create a new Google Maps object
      this.map = new google.maps.Map(document.getElementById("explore-map"), {
        center: this.mapCenterCoordinates,
        zoom: this.mapCenterZoom,
        mapTypeId: "hybrid",
        styles: [
          {
            featureType: "poi",
            elementType: "labels.text",
            stylers: [
              {
                visibility: "off",
              },
            ],
          },
          {
            featureType: "poi.medical",
            stylers: [
              {
                visibility: "off",
              },
            ],
          },
          {
            featureType: "poi.business",
            stylers: [
              {
                visibility: "off",
              },
            ],
          },
          {
            featureType: "road",
            elementType: "labels.icon",
            stylers: [
              {
                visibility: "off",
              },
            ],
          },
          {
            featureType: "transit",
            stylers: [
              {
                visibility: "off",
              },
            ],
          },
        ],
      });

      let pins = [];
      // Loop through array of (pin) objects
      for (let i = 0; i < array.length; i++) {
        let group = [];
        let match = false;
        // Only run if there is information to compare
        if (array.length > 1) {
          // Loop through to find matches
          // Start from next item in array
          for (let j = i + 1; j < array.length; j++) {
            // If latitude matches, check longitude
            if (array[i].latitude === array[j].latitude) {
              if (array[i].longitude === array[j].longitude) {
                match = true;
                group.push(array[j]);
                // Remove matched item
                array.splice(j, 1);
                // Move j back one after splicing
                j -= 1;
              }
            }
          }
        }
        if (match) {
          // Include initial comparison item in array
          group.unshift(array[i]);
          // Push array of matches into pins array
          pins.push(group);
          // Reset group and match variables
          group = [];
          match = false;
        } else {
          // If no matches, push in pin by itself
          pins.push([array[i]]);
        }
      }

      let infoWindow = new google.maps.InfoWindow();
      let marker, z;
      // Bounds automatically calculates map size, fits all pins in window
      let bounds = new google.maps.LatLngBounds();
      for (z = 0; z < pins.length; z++) {
        marker = new google.maps.Marker({
          // [0] because all coordinates in array[z] are the same
          position: new google.maps.LatLng(
            pins[z][0].latitude,
            pins[z][0].longitude
          ),
          map: this.map,
          icon: require("../assets/pointer.png"),
        });
        bounds.extend(marker.position);
        if (!this.monument) {
          google.maps.event.addListener(
            marker,
            "click",
            ((marker, z) => {
              return () => {
                let text = "";
                // If there is more than one pin with the same coordinates
                if (pins[z].length > 1) {
                  // Create link for every "pin" result for combining display in infoWindow
                  for (let i = 0; i < pins[z].length; i++) {
                    // If Sub-Itinerary level, add Location name
                    if (this.subItinerary) {
                      text =
                        text +
                        `
                      <div>
                        <a href="${this.$route.path}/${pins[z][i].id}">
                          ${pins[z][i].location}: ${pins[z][i].name}
                        </a>
                      </div>
                    `;
                    } else {
                      let path = this.$route.path;
                      // if (this.route) {
                      //   let last = path.lastIndexOf("/");
                      //   path = path.slice(0, last);
                      // }
                      text =
                        text +
                        `
                      <div>
                        <a href="${path}/${pins[z][i].id}">
                          ${pins[z][i].name}
                        </a>
                      </div>
                    `;
                    }
                  }
                } else {
                  if (this.subItinerary) {
                    text = `
                    <div>
                      <a href="${this.$route.path}/${pins[z][0].id}">
                        ${pins[z][0].location}: ${pins[z][0].name}
                      </a>
                    </div>
                  `;
                  } else {
                    let path = this.$route.path;
                    // if (this.route) {
                    //   let last = path.lastIndexOf("/");
                    //   path = path.slice(0, last);
                    // }
                    // [0] because it's the only option
                    text = `
                    <a href="${path}/${pins[z][0].id}">
                    ${pins[z][0].name}
                    </a>
                  `;
                  }
                }
                infoWindow.setContent(text);
                infoWindow.open(this.map, marker);
                this.map.panTo(
                  new google.maps.LatLng(
                    pins[z][0].latitude,
                    pins[z][0].longitude
                  )
                );
              };
            })(marker, z)
          );
        }
      }
      this.map.fitBounds(bounds);
      google.maps.event.addListenerOnce(this.map, "idle", () => {
        if (this.map.getZoom() > this.mapCenterZoom) {
          this.map.setZoom(this.mapCenterZoom);
        }
      });
    },
  },
  created() {
    // Insert citation link when copy/pasting from site
    window.addEventListener("copy", (e) => {
      let selection = document.getSelection();
      let citation = `\n\nSource: [${window.location.href}]`;
      e.clipboardData.setData("text/plain", selection + citation);
      e.preventDefault();
    });
  },
  mounted() {
    this.getData();
  },
  beforeRouteUpdate(to, from, next) {
    let array = Object.values(to.params);
    to.params.theme = null;
    to.params.country = null;
    to.params.filter = null;
    to.params.territory = null;
    to.params.itinerary = null;
    to.params.subItinerary = null;
    to.params.location = null;
    to.params.route = null;
    to.params.monument = null;
    to.params.language = null;
    to.params.tab = null;
    for (let i = 0; i < array.length; i++) {
      if (!array[i]) {
        continue;
      }
      if (array[i].includes("t-")) {
        to.params.theme = array[i];
      } else if (array[i].includes("c-")) {
        to.params.country = array[i];
      } else if (array[i].includes("f-")) {
        to.params.filter = array[i];
      } else if (array[i].includes("tr-")) {
        to.params.territory = array[i];
      } else if (array[i].includes("si-")) {
        to.params.subItinerary = array[i];
      } else if (array[i].includes("i-")) {
        to.params.itinerary = array[i];
      } else if (array[i].includes("l-")) {
        to.params.location = array[i];
      } else if (array[i].includes("r-")) {
        to.params.route = array[i];
      } else if (array[i].includes("m-")) {
        to.params.monument = array[i];
      } else if (array[i].includes("lan-")) {
        to.params.language = array[i];
      } else if (array[i].includes("tab-")) {
        to.params.tab = array[i];
      }
    }
    // Keep Route information in URL if going to a Monument page
    // If going from Monument to Monument, must be via side navigation, therefore remove Route
    if (from.params.route && !to.params.route && to.params.monument && !from.params.monument) {
      to.params.route = from.params.route;
    }
    // Skip Theme "level" if there is only one Country
    if (
      this.$route.name === "themes" &&
      from.params.theme !== to.params.theme
    ) {
      let theme = this.strip(to.params.theme);
      let url = `${process.env.VUE_APP_API_ENDPOINT}/themes/${theme}`;
      axios({ method: "GET", url: url }).then(
        (result) => {
          // Check if there is only one Country entry in the Theme
          if (result.data.countries.length === 1) {
            to.params.country = `c-${result.data.countries[0].countryId}`;
          }
          next();
        },
        (error) => {
          console.error(error);
        }
      );
    } else {
      next();
    }
  },
};
</script>

<style scoped lang="scss">
.loader-container {
  height: 500px;
}

.bold {
  font-weight: bold;
}

#wrapper {
  width: 100%;
}

#explore-container {
  display: flex;
  background-color: white;
  width: 100%;
}

#side-navigation-container {
  position: relative;
  width: 25%;
  padding-top: 30px;
  padding-left: 30px;
}

#explore {
  width: 75%;
  padding: 0 40px;
}
#breadcrumb {
  @include font-size-7;
  margin-top: 30px;
  margin-bottom: 20px;
}
#breadcrumb a {
  color: $medium-green;
}

#suggested-routes-link a {
  @include font-size-7;
  font-weight: bold;
  color: $dark-green;
  border: 2px solid $dark-green;
  border-radius: 5px;
  padding: 2px 5px;
}

#explore-header {
  @include headings;
  @include font-size-2;
  margin-top: 40px;
  margin-bottom: 30px;
}
#monument-location {
  font-weight: normal;
}

#extra-theme-info-wrapper {
  margin-bottom: 20px;
}
#territories-list {
  margin-bottom: 5px;
}
#territories-list span,
#single-country-theme-name {
  @include texts;
  @include font-size-6;
  font-weight: bold;
}

#explore-description {
  margin-bottom: 40px;
}
#explore-description span,
#explore-description span :deep(*) {
  @include texts;
  @include font-size-6;
}
#explore-description span :deep(a) {
  @include link-styles;
}
.description-language-selection {
  margin-bottom: 10px;
}

#explore-menu-container {
  position: relative;
  height: 55px;
  width: 100%;
  border-bottom: 2px solid $medium-light-grey;
  margin-bottom: 20px;
}
#explore-menu {
  @include flex-center;
  position: absolute;
  top: 0;
  width: 100%;
}
.explore-menu-item {
  @include flex-center;
  @include headings;
  @include font-size-5;
  height: 50px;
  width: 150px;
  text-align: center;
  color: $medium-light-grey;
  padding-bottom: 12px;
  margin: 5px 10px 0 10px;
  border-bottom: 5px solid transparent;
  cursor: pointer;
}
.selected {
  color: $dark-green;
  border-bottom: 5px solid $dark-green;
}
#explore-directions * {
  @include texts;
  @include font-size-6;
}
#other-contact .monument-field {
  margin-bottom: 0;
}
#location-background-label {
  font-weight: bold;
  margin-bottom: 20px;
}
#location-background-container,
#alt-location-background-container {
  margin-bottom: 20px;
}
#location-background-container :deep(*),
#alt-location-background-container :deep(*) {
  @include texts;
  @include font-size-6;
}
#location-background-language-selection,
#alt-location-background-language-selection {
  margin-bottom: 20px;
}
#alt-location-background-container a {
  display: inline-block;
  font-weight: bold;
  margin-top: 20px;
}

.explore-additional {
  margin-bottom: 40px;
}
.explore-additional * {
  @include texts;
  @include font-size-6;
}
.additional-label,
.additional-intro {
  margin-bottom: 20px;
}
.accommodations-hotel,
.guided-visits-contact,
.useful-websites-site {
  margin-bottom: 20px;
}
.hotel-category {
  font-style: italic;
}
.hotel-url a,
.useful-websites-url a {
  @include link-styles;
}

#explore-map-container {
  width: 100%;
  margin-top: 30px;
}
#explore-map-header {
  @include headings;
  @include font-size-3;
  margin-top: 40px;
  margin-bottom: 10px;
}
#explore-map {
  height: 500px;
  width: 100%;
  margin-bottom: 40px;
}

.explore-routes-header {
  @include headings;
  @include font-size-3;
  margin-bottom: 20px;
}
#suggested-routes {
  display: inline-block;
  cursor: default;
}
  #suggested-routes:hover {
    text-decoration: none;
  }
#explore-routes-description {
  margin-bottom: 20px;
}
#explore-routes-description span {
  font-weight: bold;
}
.routes-select-container {
  margin-bottom: 20px;
}

#return-route-link a {
  @include font-size-7;
  display: inline-block;
  font-weight: bold;
  color: white;
  background-color: $dark-green;
  border-radius: 5px;
  padding: 2px 5px;
  margin-top: 15px;
}

#explore-next-container {
  width: 100%;
}
#explore-itineraries-container {
  margin-bottom: 20px;
}
.explore-next-header {
  @include headings;
  @include font-size-3;
  margin-bottom: 20px;
}
.explore-next-tiles {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 20px;
}
.explore-next-tile {
  @include flex-center;
  height: 200px;
  width: 100%;
  position: relative;
  text-align: center;
  border-radius: 10px;
  background-color: $dark-green;
  box-shadow: inset 0 0 0 100px rgba($dark-grey, 0.3);
  padding: 20px;
}
.explore-next-tiles .country-tiles {
  background-position: center;
  background-size: cover;
  box-shadow: inset 0 0 0 100px rgba($dark-grey, 0.5);
}
.explore-next-tiles .map-tiles {
  background-position: center;
  background-size: cover;
}
.explore-next-tiles .monument-tiles {
  box-shadow: none;
  background-position: center;
  background-size: cover;
  margin-bottom: 50px;
}
.explore-next-link {
  height: 100%;
  width: 100%;
  position: absolute;
}
.monument-next-label {
  @include font-size-6;
  position: absolute;
  top: 100%;
  left: 0;
  font-weight: bold;
  text-align: left;
  padding-top: 5px;
}
.monument-next-label a {
  color: $dark-green;
}
.explore-next-label {
  @include font-size-2;
  font-weight: bold;
  color: white;
}
/* IE-only */
@media only screen and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .explore-next-tiles {
    display: flex;
    flex-wrap: wrap;
  }
  .explore-next-tile {
    width: 32%;
    margin: 5px;
  }
  .explore-next-link {
    top: 0;
    left: 0;
  }
}

.explore-instead {
  display: inline-block;
  padding-bottom: 15px;
}

@media only screen and (max-width: 1199px) {
  #explore-container {
    flex-direction: column;
  }
  #side-navigation-container {
    width: 100%;
    padding: 40px 40px 0 40px;
  }
  #explore {
    width: 100%;
  }
}

@media only screen and (max-width: 949px) {
  #explore-header {
    @include font-size-3;
  }
  .explore-next-tiles {
    grid-template-columns: repeat(2, 1fr);
  }
  .explore-next-label {
    @include font-size-3;
  }
  /* IE-only */
  @media only screen and (-ms-high-contrast: none),
    (-ms-high-contrast: active) {
    .explore-next-tiles {
      display: flex;
      flex-wrap: wrap;
    }
    .explore-next-tile {
      width: 48%;
      margin: 5px;
    }
  }
}

@media only screen and (max-width: 649px) {
  .explore-next-tile {
    height: 150px;
  }
  .explore-next-label {
    @include font-size-4;
  }
  /* IE-only */
  @media only screen and (-ms-high-contrast: none),
    (-ms-high-contrast: active) {
    .explore-next-tile {
      width: 46%;
    }
  }
}

@media only screen and (max-width: 499px) {
  #side-navigation-container {
    padding: 30px 30px 0 30px;
  }
  #explore {
    padding: 0 30px;
  }
  #explore-header,
  .explore-next-header {
    @include font-size-4;
  }
  #explore-header {
    margin-top: 20px;
  }
  #explore-map-container {
    margin-top: 20px;
  }
  #explore-map {
    height: 400px;
  }
  .explore-next-tiles {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  .explore-next-tile {
    width: 100%;
  }
  .explore-next-tiles .monument-tiles {
    margin-bottom: 30px;
  }
  /* IE-only */
  @media only screen and (-ms-high-contrast: none),
    (-ms-high-contrast: active) {
    .explore-next-tile {
      width: 100%;
    }
  }
}
</style>
